import React, { type FC, useEffect, useMemo, useState } from 'react';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useDrivers } from '../hooks/useDrivers';
import { useStores } from '../hooks/useStores';
import StatusBadge from '../components/StatusBadge';
import CreateDriverModal from '../components/CreateDriverModal';
import DeleteDriverModal from '../components/DeleteDriversModal.tsx';
import TablePagination from '../components/TablePagination';
import type { Driver } from '../types';
import type { DriverDeliveryStatus } from '../services/driverService';

type DriverLite = Driver & {
  cellphone?: string;
  last_active_at?: string | null;
};

const ITEMS_PER_PAGE = 25;
const FIELD = 'w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] focus:outline-none bg-white';
const FINAL_STATUSES = new Set(['DELIVERY_COMPLETE', 'DELIVERY_FAILED', 'ORDER_CANCELLED']);

const Drivers: FC = () => {
  const { drivers, deliveryStatuses, loading, fetchAll, fetchDriverPhone, updateDriver, deleteDriver } = useDrivers();
  const { stores, fetchStores } = useStores();

  const [q,                  setQ]                  = useState('');
  const [currentPage,        setCurrentPage]        = useState(1);
  const [isAddModalOpen,     setIsAddModalOpen]     = useState(false);
  const [isDeleteModalOpen,  setIsDeleteModalOpen]  = useState(false);
  const [driverToDelete,     setDriverToDelete]     = useState<DriverLite | null>(null);
  const [deleteLoading,      setDeleteLoading]      = useState(false);
  const [isEditOpen,         setIsEditOpen]         = useState(false);
  const [editDriver,         setEditDriver]         = useState<DriverLite | null>(null);
  const [saving,             setSaving]             = useState(false);
  const [phoneLoading,       setPhoneLoading]       = useState(false);

  useEffect(() => {
    void Promise.all([fetchAll(), fetchStores()]);
  }, [fetchAll, fetchStores]);

  const getStoreName = (storeId?: string | number) => {
    if (!storeId) return '—';
    return stores.find(s => String(s.id) === String(storeId))?.name ?? 'Unknown Store';
  };

  const activeByDriver = useMemo(() => {
    const by: Record<string, DriverDeliveryStatus[]> = {};
    deliveryStatuses.forEach(d => {
      const driverId = d.driver_id == null ? null : String(d.driver_id);
      const s = (d.status ?? '').toUpperCase();
      if (driverId && !FINAL_STATUSES.has(s)) {
        if (!by[driverId]) by[driverId] = [];
        by[driverId].push(d);
      }
    });
    Object.keys(by).forEach(key => {
      by[key].sort((a, b) =>
          new Date(b.assigned_at ?? b.created_at ?? 0).getTime() -
          new Date(a.assigned_at ?? a.created_at ?? 0).getTime()
      );
    });
    return by;
  }, [deliveryStatuses]);

  const currentStatus = (driver: Driver) => {
    const key = driver.auth_id == null ? null : String(driver.auth_id);
    const actives = key ? activeByDriver[key] : undefined;
    if (!actives || actives.length === 0) return 'ASSIGN_DRIVER';
    return (actives[0].status ?? 'DRIVER_ASSIGNED').toUpperCase();
  };

  const driverLastActive = (driver: DriverLite) => {
    const key = driver.auth_id == null ? null : String(driver.auth_id);
    const actives = key ? activeByDriver[key] : undefined;
    if (actives && actives.length > 0) {
      const top = actives[0];
      const iso = top.assigned_at ?? top.created_at;
      return iso ? new Date(iso).toLocaleString() : '–';
    }
    return driver.last_active_at ? new Date(driver.last_active_at).toLocaleString() : '–';
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return drivers as DriverLite[];
    const needle = q.toLowerCase();
    return (drivers as DriverLite[]).filter(d =>
        (d.driver_name ?? '').toLowerCase().includes(needle) ||
        (d.number_plate ?? '').toLowerCase().includes(needle) ||
        (d.car_model ?? '').toLowerCase().includes(needle)
    );
  }, [drivers, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated  = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // ── Edit ──────────────────────────────────────────────────────────────────

  const openEdit = async (driver: Driver) => {
    setPhoneLoading(true);
    setEditDriver({
      ...driver,
      driver_name:  driver.driver_name  ?? '',
      car_model:    driver.car_model    ?? '',
      number_plate: driver.number_plate ?? '',
      cellphone: '',
    });
    setIsEditOpen(true);
    if (driver.auth_id) {
      const phone = await fetchDriverPhone(driver.auth_id);
      setEditDriver(p => p ? { ...p, cellphone: phone } : p);
    }
    setPhoneLoading(false);
  };

  const closeEdit = () => { setIsEditOpen(false); setEditDriver(null); };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editDriver?.id) return;
    setSaving(true);
    try {
      const updated = await updateDriver(
          String(editDriver.id),
          {
            driver_name:  editDriver.driver_name  ?? '',
            car_model:    editDriver.car_model    ?? '',
            number_plate: editDriver.number_plate ?? '',
            store_id:     editDriver.store_id,
          },
          editDriver.cellphone,
          editDriver.auth_id
      );
      if (updated) closeEdit();
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const selectedDriverDeliveryCount = useMemo(() => {
    if (!driverToDelete) return 0;
    const uuid = String(driverToDelete.auth_id);
    return deliveryStatuses.filter(d =>
        String(d.driver_id) === uuid && !FINAL_STATUSES.has((d.status ?? '').toUpperCase())
    ).length;
  }, [driverToDelete, deliveryStatuses]);

  const openDeleteConfirm = (driver: DriverLite) => { setDriverToDelete(driver); setIsDeleteModalOpen(true); };

  const confirmDelete = async () => {
    const authUuid = driverToDelete?.auth_id;
    if (!authUuid) return;
    setDeleteLoading(true);
    try {
      const ok = await deleteDriver(String(authUuid));
      if (ok) { setIsDeleteModalOpen(false); setDriverToDelete(null); }
      else alert('Could not delete driver.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Drivers</h1>
            <p className="text-sm text-stone-500 mt-1">Current fleet status and details.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input type="search" value={q} onChange={e => { setQ(e.target.value); setCurrentPage(1); }}
                     placeholder="Search drivers, plates, status…"
                     className="w-64 rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-white pl-9 pr-3 py-2.5 text-sm placeholder:text-stone-400 focus:ring-2 focus:ring-[#ee2b74] focus:outline-none" />
            </div>
            <button onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] px-4 py-2.5 text-sm font-medium text-white transition shadow-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Driver
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-100">
            <p className="text-sm text-stone-500">
              {loading ? 'Loading…' : `${filtered.length} driver${filtered.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {['Driver', 'Phone', 'Vehicle', 'Status', 'Store', 'Last active', 'Actions'].map(h => (
                    <th key={h} className={`px-5 py-3 text-xs font-semibold uppercase tracking-wide text-stone-500 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
              {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        {Array.from({ length: 7 }).map((_, j) => (
                            <td key={j} className="px-5 py-4"><div className="h-4 bg-stone-100 rounded" /></td>
                        ))}
                      </tr>
                  ))
              ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-sm text-center text-stone-400">
                      {q ? `No drivers match "${q}"` : 'No drivers found.'}
                    </td>
                  </tr>
              ) : paginated.map(d => {
                const status = currentStatus(d);
                return (
                    <tr key={String(d.id ?? d.auth_id)} className="hover:bg-stone-50 transition-colors">
                      <td className="px-5 py-3.5 font-medium text-stone-900">{d.driver_name ?? '—'}</td>
                      <td className="px-5 py-3.5 text-stone-500 text-xs">{d.cellphone ?? '—'}</td>
                      <td className="px-5 py-3.5 text-stone-600">
                        {[d.car_model, d.number_plate].filter(Boolean).join(' • ') || '—'}
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={status} /></td>
                      <td className="px-5 py-3.5 text-stone-600">{getStoreName(d.store_id)}</td>
                      <td className="px-5 py-3.5 text-stone-500 text-xs">{driverLastActive(d)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          <button type="button" onClick={() => void openEdit(d)}
                                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-stone-100 text-stone-700 text-xs font-medium hover:bg-stone-200 transition">
                            <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
                          </button>
                          <button type="button" onClick={() => openDeleteConfirm(d)}
                                  className="inline-flex items-center rounded-lg p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 transition">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                );
              })}
              </tbody>
            </table>
          </div>
          <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filtered.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={page => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        </div>

        {/* Edit modal */}
        {isEditOpen && editDriver && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeEdit} />
              <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-stone-200 overflow-hidden">
                <div className="h-1 w-full bg-[#ee2b74]" />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-stone-900">Edit Driver</h3>
                  <p className="text-xs text-stone-500 mt-0.5">{editDriver.driver_name}</p>
                  <form className="mt-5 space-y-4" onSubmit={saveEdit}>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Name</label>
                      <input type="text" required className={FIELD}
                             value={editDriver.driver_name ?? ''}
                             onChange={e => setEditDriver(p => p ? { ...p, driver_name: e.target.value } : p)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Phone Number</label>
                      <input type="tel" className={FIELD}
                             value={editDriver.cellphone ?? ''}
                             onChange={e => setEditDriver(p => p ? { ...p, cellphone: e.target.value } : p)}
                             placeholder={phoneLoading ? 'Loading…' : 'e.g. 082 123 4567'}
                             disabled={phoneLoading} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Assigned Store</label>
                      <div className="relative">
                        <select className={`${FIELD} appearance-none pr-10`} required
                                value={editDriver.store_id ?? ''}
                                onChange={e => setEditDriver(p => p ? { ...p, store_id: e.target.value } : p)}>
                          <option value="" disabled>Select a store</option>
                          {stores.map(s => (
                              <option key={String(s.id)} value={String(s.id)}>{s.name}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-stone-400">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1.5">Car model</label>
                        <input type="text" className={FIELD}
                               value={editDriver.car_model ?? ''}
                               onChange={e => setEditDriver(p => p ? { ...p, car_model: e.target.value } : p)} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1.5">Number plate</label>
                        <input type="text" className={FIELD}
                               value={editDriver.number_plate ?? ''}
                               onChange={e => setEditDriver(p => p ? { ...p, number_plate: e.target.value } : p)} />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-stone-100">
                      <button type="button" onClick={closeEdit} disabled={saving}
                              className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50 transition">
                        Cancel
                      </button>
                      <button type="submit" disabled={saving}
                              className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition">
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}

        <DeleteDriverModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            driverName={driverToDelete?.driver_name ?? ''}
            deliveryCount={selectedDriverDeliveryCount}
            loading={deleteLoading}
        />
        <CreateDriverModal
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            onRefresh={fetchAll}
        />
      </div>
  );
};

export default Drivers;